import { Flex, FlexProps, SimpleGrid, Text } from '@chakra-ui/react'
import { Collapsible, FieldMapper, Menu } from 'components'
import { useAlert } from 'components/providers'
import { isEqual, startCase, xor } from 'lodash'
import { useEffect, useRef, useState } from 'react'
import { primaryLeadProperties } from 'types'

interface Props extends Omit<FlexProps, 'onChange'> {
  _ref?: React.Ref<HTMLDivElement>
  /** if !properties, ['firstName', 'lastName', 'email', 'phone'] */
  defaultProperties?: string[]
  /** default primaryLeadProperties */
  properties?: string[]
  /** default "File Fields" */
  inputFieldLabel?: string
  inputMap: Record<string, string>
  onChange: (fieldMap?: Record<string, any>) => void
}

export const defaultPrimaryProperties = ['firstName', 'lastName', 'email', 'phone']

export default function PropertyMapper({
  defaultProperties: _defaultProperties,
  inputFieldLabel = 'File Fields',
  inputMap,
  properties = primaryLeadProperties,
  onChange,
  _ref,
  ...rest
}: Props) {
  const [leadMapping, setLeadMapping] = useState({} as Record<string, any>)
  const { alert } = useAlert()

  const defaultProperties =
    _defaultProperties ||
    (isEqual(properties, primaryLeadProperties) ? defaultPrimaryProperties : properties)
  const mappedProperties = Object.keys(leadMapping) as string[]
  const nonDefaultMappedProperties = mappedProperties.filter(
    key => !defaultProperties.includes(key)
  )
  const mappedInputs = (Object.values(leadMapping) as string[]).filter(Boolean)
  const visibleProperties = [...defaultProperties, ...nonDefaultMappedProperties]
  const repeatedInputs = mappedInputs.sort().filter((val, i) => mappedInputs.includes(val, i + 1))
  const hiddenFields = xor(properties, visibleProperties)
  const customProperties = Object.keys(inputMap || {}).filter(
    key => !Object.values(leadMapping).includes(key)
  )

  useEffect(() => {
    if (repeatedInputs.length) {
      onChange()
    } else {
      const cleaned = Object.entries(leadMapping).reduce((acc, [key, value]) => {
        if (value === '') return acc
        return { ...acc, [key]: value }
      }, {} as Record<string, any>)
      onChange(cleaned)
    }
  }, [leadMapping])

  useEffect(() => {
    const autoMapped = Object.keys(inputMap).reduce((acc, inputProperty) => {
      const closestProperty = getClosestProperty(inputProperty, properties)
      if (!closestProperty) return acc

      const currentValue = acc[closestProperty]
      if (currentValue && currentValue.length < inputProperty.length) return acc

      return { ...acc, [closestProperty]: inputProperty }
    }, {} as Record<string, any>)
    if (isEqual(autoMapped, leadMapping)) return

    setLeadMapping(autoMapped)
  }, [inputMap])

  const alertShown = useRef(false)
  useEffect(() => {
    if (!repeatedInputs.length || alertShown.current) return

    alertShown.current = true
    alert('Please map each field to a unique value')
  }, [repeatedInputs])

  function updateLeadMapping(property: string, input: string) {
    const updated = { ...leadMapping, [property]: input }
    setLeadMapping(updated)
  }

  return (
    <Flex ref={_ref} direction="column" gap={3} py={4} px={5} {...rest}>
      <SimpleGrid w="100%" columns={3} spacing={3}>
        <Text textStyle="bodySmall">Momentify Fields</Text>
        <Text textStyle="bodySmall">{inputFieldLabel}</Text>
        <Text textStyle="bodySmall">Scanned Data Input</Text>
      </SimpleGrid>

      {visibleProperties.map(field => (
        <FieldMapper
          error={repeatedInputs.some(
            fields => leadMapping[field] && fields.includes(leadMapping[field]!)
          )}
          key={field}
          onChange={value => updateLeadMapping(field, value)}
          referenceField={field}
          targetFieldsAndValues={inputMap}
          value={leadMapping[field] || ''}
        />
      ))}

      {hiddenFields.length > 0 && (
        <SimpleGrid w="100%" columns={3} spacing={3}>
          <Menu
            placeholder="Other Properties"
            variant="outline"
            noPortal
            borderRadius="sm"
            options={hiddenFields.map(field => ({ label: startCase(field), value: field }))}
            onChange={value => updateLeadMapping(value, '')}
          />
        </SimpleGrid>
      )}

      {customProperties.length > 0 && (
        <Collapsible
          label={`Custom Fields ${
            customProperties.length > 0 ? ` (${customProperties.length})` : ''
          }`}
        >
          <Flex direction="column" gap={3}>
            <Text textStyle="bodyXSmall" opacity={0.6} fontWeight="normal">
              Any additional fields brought in from data that does not match the mapping above will
              appear here.
            </Text>
            <Flex w="100%" gap={3}>
              <Text textStyle="bodySmall" w="60%">
                Field Name
              </Text>
              <Text textStyle="bodySmall" w="40%">
                Field Value
              </Text>
            </Flex>
            {customProperties.map(property => (
              <Flex key={property} w="100%" gap={3}>
                <Text w="60%" {...textStyling}>
                  {property}
                </Text>
                <Text {...textStyling} w="40%">
                  {inputMap?.[property]}
                </Text>
              </Flex>
            ))}
          </Flex>
        </Collapsible>
      )}
    </Flex>
  )
}

const textStyling = {
  bg: 'rgba(23, 23, 23, 0.10)',
  borderRadius: '10px',
  minW: '100px',
  h: '40px',
  py: '10px',
  px: '16px',
  textStyle: 'bodyMedium',
  fontWeight: '500',
  isTruncated: true,
}

function getNormalizedKey(key: string) {
  return key.toLowerCase().replace(/[\s_-]+/g, '')
}

function getClosestProperty(input: string, properties: string[]) {
  const normalizedKey = getNormalizedKey(input)
  let mostSimilarMatch = ''

  for (const property of properties) {
    const propertyLower = getNormalizedKey(property)
    if (propertyLower === normalizedKey) return property
    if (normalizedKey.includes(propertyLower)) mostSimilarMatch = property
  }

  return mostSimilarMatch
}
